import { useState, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Badge,
  Alert,
  Button,
  ButtonGroup,
  FormGroup,
  Label,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  usePostmasterIncidents,
  useUpdatePostmasterIncident,
  useRetryDelisting,
} from './useNexusPostmaster';
import { RblProvidersTab } from './RblProvidersTab';
import { RblServerIpsTab } from './RblServerIpsTab';

const STATUS_COLORS = {
  listed: 'danger',
  mitigation_active: 'warning',
  delisting_requested: 'info',
  cleared: 'success',
};

const STATUSES = ['', 'listed', 'mitigation_active', 'delisting_requested', 'cleared'];
const PROVIDERS = ['', 'microsoft', 'spamhaus', 'barracuda', 'google'];

/**
 * Nexus Postmaster SOC Dashboard (Spec 7 von 8).
 *
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-postmaster-soc.md
 * Endpoints: /v1/admin/postmaster/incidents
 *
 * REST + 15s-Polling; WebSocket-Push folgt in Iteration 2 per INBOX-Hinweis.
 * Delisting-Button funktioniert Backend-seitig schon; Sub-Plugins
 * (delisting-microsoft, ...) sind separate Folge-Tickets.
 */
export function NexusPostmasterPage() {
  const [tab, setTab] = useState('incidents');
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [ip, setIp] = useState('');
  const [openIncident, setOpenIncident] = useState(null);
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  const query = usePostmasterIncidents({ status, provider, ip });
  const incidents = query.data?.incidents ?? [];
  // dataUpdatedAt ist stabile Render-Quelle, anders als Date.now() —
  // React-Compiler-strict erlaubt sie als useMemo-Dep.
  const updatedAt = query.dataUpdatedAt ?? 0;

  const counters = useMemo(() => {
    const c = { listed: 0, mitigation_active: 0, delisting_requested: 0, cleared24h: 0 };
    const cutoff = updatedAt - 24 * 3600_000;
    for (const i of incidents) {
      if (i.status === 'listed') c.listed += 1;
      else if (i.status === 'mitigation_active') c.mitigation_active += 1;
      else if (i.status === 'delisting_requested') c.delisting_requested += 1;
      else if (i.status === 'cleared' && i.cleared_at && new Date(i.cleared_at).getTime() > cutoff) {
        c.cleared24h += 1;
      }
    }
    return c;
  }, [incidents, updatedAt]);

  return (
    <div>
      <PageHeader
        title="Nexus · Postmaster SOC"
        subtitle={`${incidents.length} Incidents · Auto-Refresh 15s · WebSocket-Push folgt`}
      />

      <Nav tabs className="mb-3">
        <NavItem>
          <NavLink
            active={tab === 'incidents'}
            onClick={() => setTab('incidents')}
            role="button"
          >
            Vorfälle
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            active={tab === 'rbls'}
            onClick={() => setTab('rbls')}
            role="button"
          >
            RBLs
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            active={tab === 'ips'}
            onClick={() => setTab('ips')}
            role="button"
          >
            Server-IPs
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={tab}>
        <TabPane tabId="incidents">

      <Alert color="info" className="small">
        Vorfälle aus dem IMAP-Scanner gegen NDR-Pattern verschiedener Provider.
        Sub-Plugins für automatisches Delisting (Microsoft / Barracuda /
        Spamhaus) sind Folge-Tickets — der Retry-Button setzt schon jetzt den
        Status auf <code>delisting_requested</code>.
      </Alert>

      <Row className="g-3 mb-3">
        <Col md="3">
          <CounterTile label="Listed" value={counters.listed} color="danger" />
        </Col>
        <Col md="3">
          <CounterTile label="Mitigation aktiv" value={counters.mitigation_active} color="warning" />
        </Col>
        <Col md="3">
          <CounterTile label="Delisting läuft" value={counters.delisting_requested} color="info" />
        </Col>
        <Col md="3">
          <CounterTile label="Cleared (24h)" value={counters.cleared24h} color="success" />
        </Col>
      </Row>

      <Card className="mb-3">
        <CardBody className="py-2">
          <div className="d-flex flex-wrap gap-3 align-items-end">
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Status</Label>
              <Input type="select" bsSize="sm" value={status} onChange={(e) => setStatus(e.target.value)} style={{ minWidth: 200 }}>
                {STATUSES.map((s) => (
                  <option key={s || 'all'} value={s}>{s || 'Alle'}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Provider</Label>
              <Input type="select" bsSize="sm" value={provider} onChange={(e) => setProvider(e.target.value)} style={{ minWidth: 160 }}>
                {PROVIDERS.map((p) => (
                  <option key={p || 'all'} value={p}>{p || 'Alle'}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">IP</Label>
              <Input type="text" bsSize="sm" value={ip} onChange={(e) => setIp(e.target.value)} placeholder="z.B. 198.51." style={{ minWidth: 160 }} />
            </FormGroup>
          </div>
        </CardBody>
      </Card>

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && incidents.length === 0}
        emptyMessage="Keine Incidents für die aktuellen Filter — keine Listings 🎉"
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 70 }}>ID</th>
            <th style={{ width: 170 }}>IP</th>
            <th style={{ width: 130 }}>Provider</th>
            <th style={{ width: 170 }}>Status</th>
            <th>Evidence</th>
            <th style={{ width: 130 }}>Detected</th>
            <th style={{ width: 130 }}>Cleared</th>
          </tr>
        </thead>
        <tbody>
          {incidents.map((i) => (
            <tr key={i.id} role="button" onClick={() => setOpenIncident(i)} style={{ cursor: 'pointer' }}>
              <td><code className="small">{i.id}</code></td>
              <td className="font-monospace small">{i.ip_address}</td>
              <td>
                <Badge color="secondary" pill className="orbit-code-badge-sm">{i.provider}</Badge>
              </td>
              <td>
                <Badge color={STATUS_COLORS[i.status] ?? 'secondary'} pill>{i.status}</Badge>
              </td>
              <td className="small text-truncate" style={{ maxWidth: 320 }}>
                <code title={i.evidence?.raw}>{i.evidence?.code ?? '—'}</code>
              </td>
              <td>{i.detected_at ? <RelativeTime iso={i.detected_at} /> : <span className="text-muted">—</span>}</td>
              <td>{i.cleared_at ? <RelativeTime iso={i.cleared_at} /> : <span className="text-muted">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      <IncidentDrawer
        incident={openIncident}
        isOpen={!!openIncident}
        onClose={() => setOpenIncident(null)}
        canWrite={canWrite}
      />

        </TabPane>
        <TabPane tabId="rbls">
          {tab === 'rbls' && <RblProvidersTab canWrite={canWrite} />}
        </TabPane>
        <TabPane tabId="ips">
          {tab === 'ips' && <RblServerIpsTab canWrite={canWrite} />}
        </TabPane>
      </TabContent>
    </div>
  );
}

function CounterTile({ label, value, color }) {
  return (
    <Card className="h-100">
      <CardBody>
        <div className={`small text-${color}-emphasis fw-semibold`}>{label}</div>
        <div className="display-6 fw-bold">{value}</div>
      </CardBody>
    </Card>
  );
}

function IncidentDrawer({ incident, isOpen, onClose, canWrite }) {
  const update = useUpdatePostmasterIncident();
  const retryDelisting = useRetryDelisting();

  if (!incident) return null;

  const onMarkCleared = async () => {
    await update.mutateAsync({ id: incident.id, status: 'cleared' });
    onClose();
  };
  const onRetryDelisting = async () => {
    await retryDelisting.mutateAsync({ id: incident.id });
    onClose();
  };

  return (
    <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" style={{ width: 560 }}>
      <OffcanvasHeader toggle={onClose}>
        Incident · {incident.ip_address}
      </OffcanvasHeader>
      <OffcanvasBody>
        <dl className="row small">
          <dt className="col-4 text-muted">ID</dt>
          <dd className="col-8"><code>{incident.id}</code></dd>
          <dt className="col-4 text-muted">IP</dt>
          <dd className="col-8"><code className="font-monospace">{incident.ip_address}</code></dd>
          <dt className="col-4 text-muted">Provider</dt>
          <dd className="col-8">
            <Badge color="secondary" pill className="orbit-code-badge-sm">{incident.provider}</Badge>
          </dd>
          <dt className="col-4 text-muted">Status</dt>
          <dd className="col-8">
            <Badge color={STATUS_COLORS[incident.status] ?? 'secondary'} pill>{incident.status}</Badge>
          </dd>
          <dt className="col-4 text-muted">Detected</dt>
          <dd className="col-8">{incident.detected_at ? <RelativeTime iso={incident.detected_at} /> : '—'}</dd>
          {incident.cleared_at && (
            <>
              <dt className="col-4 text-muted">Cleared</dt>
              <dd className="col-8"><RelativeTime iso={incident.cleared_at} /></dd>
            </>
          )}
        </dl>

        <h6 className="text-uppercase small text-muted mt-3">Evidence</h6>
        <div className="small mb-2">
          Code: <code>{incident.evidence?.code ?? '—'}</code>
        </div>
        <pre className="bg-body-tertiary p-2 small font-monospace" style={{ borderRadius: 6, maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
          {incident.evidence?.raw ?? '— kein Raw-NDR'}
        </pre>

        <HasPermission perms={['integrations.write']}>
          {canWrite && incident.status !== 'cleared' && (
            <div className="mt-4 pt-3 border-top">
              <h6 className="text-uppercase small text-muted">Operator-Aktionen</h6>
              <ButtonGroup className="d-flex flex-wrap">
                {incident.status !== 'delisting_requested' && (
                  <Button color="info" outline onClick={onRetryDelisting} disabled={retryDelisting.isPending}>
                    🔁 Delisting anfordern
                  </Button>
                )}
                <Button color="success" outline onClick={onMarkCleared} disabled={update.isPending}>
                  ✓ Manuell als cleared markieren
                </Button>
              </ButtonGroup>
              <div className="small text-muted mt-2">
                Delisting-Sub-Plugins (Microsoft / Barracuda / Spamhaus) sind
                laut INBOX Folge-Tickets — der Status-Flip läuft schon
                jetzt, der Plugin-Job-Pfad folgt.
              </div>
            </div>
          )}
        </HasPermission>
      </OffcanvasBody>
    </Offcanvas>
  );
}
